"use client";
import { motion } from "framer-motion";
import { SparklesIcon } from '@heroicons/react/24/outline';
import { useSmoothScroll } from "@/utils/smooth-scroll";

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const { scrollToSection } = useSmoothScroll({
    duration: 800,
    offset: -80,
  });

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    show: { 
      opacity: 1, 
      y: 0, 
      transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] } 
    }
  };

  const navigationLinks = [
    { name: "Accueil", href: "#hero" },
    { name: "À propos", href: "#about" },
    { name: "Le problème", href: "#problem" },
    { name: "Notre solution", href: "#solution" },
    { name: "Comment ça marche", href: "#how-it-works" },
    { name: "FAQ", href: "#faq" }
  ];

  const legalLinks = [
    { name: "Mentions légales", href: "/legal" },
    { name: "Politique de confidentialité", href: "/privacy" },
    { name: "Conditions d'utilisation", href: "/terms" }
  ];

  // Fonction pour gérer le clic sur les liens de navigation
  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    // Si c'est un lien d'ancre (commence par #), utiliser le smooth scroll
    if (href.startsWith('#')) {
      e.preventDefault();
      scrollToSection(href);
    }
    // Sinon, laisser le comportement par défaut pour les autres liens
  };

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          variants={{
            hidden: {},
            show: {
              transition: {
                staggerChildren: 0.1
              }
            }
          }}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {/* Logo et description */}
          <motion.div
            variants={fadeInUp}
            className="lg:col-span-2"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-r from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
                <SparklesIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">ConnectRH</h3>
                <p className="text-sm text-gray-400">Assistant IA RH</p>
              </div>
            </div>
            <p className="text-gray-300 leading-relaxed max-w-md">
              ConnectRH révolutionne l'onboarding et l'assistance RH grâce à une intelligence artificielle 
              spécialisée. Simplifiez l'intégration de vos collaborateurs et libérez le potentiel de votre équipe RH.
            </p>
          </motion.div>

          {/* Navigation */}
          <motion.div variants={fadeInUp}>
            <h4 className="text-lg font-semibold text-white mb-6">Navigation</h4>
            <ul className="space-y-3">
              {navigationLinks.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link.href)}
                    className="text-gray-400 hover:text-white transition-colors duration-300 text-sm cursor-pointer"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Liens légaux */}
          <motion.div variants={fadeInUp}>
            <h4 className="text-lg font-semibold text-white mb-6">Légal</h4>
            <ul className="space-y-3">
              {legalLinks.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="text-gray-400 hover:text-white transition-colors duration-300 text-sm"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
            
            {/* Contact */}
            <div className="mt-8">
              <h5 className="text-sm font-semibold text-white mb-3">Contact</h5>
              <a
                href="/contact"
                className="text-gray-400 hover:text-white transition-colors duration-300 text-sm"
              >
                Nous contacter
              </a>
            </div>
          </motion.div>
        </motion.div>

        {/* Séparateur */}
        <motion.div
          variants={fadeInUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="border-t border-gray-800 mt-12 pt-8"
        >
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              © {currentYear} ConnectRH. Tous droits réservés.
            </p>
            
            <div className="flex items-center space-x-6">
              <p className="text-gray-400 text-sm">
                💫 Made by Axiom
              </p>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-green-400 text-sm font-medium">Service actif</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  );
}
